/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  ContactRestService,
  GtoUom,
  HousePlanRestService,
  LeadRestService,
  LetterRestService,
  PriceBookRestService,
  PriceBookUsage,
  QuotationBase,
  QuotationRestService,
  SalesFolderRestService,
  SiteRestService, SystemRoleBit,
  TakeOffParamsRestService,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { AutocompleteItem, EntityVersion, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { LeadDetailsInit, leadDetailsInitFromDto, LeadListItem } from './lead.model';
import {
  CloneQuote,
  CloneSalesFolder,
  housePlanTopListFromDto,
  housePlanTopListToDto,
  QuoteApprove,
  QuoteCloneRequest,
  QuoteCloneVariationsRequest,
  quoteDetailsInitFromDto,
  QuoteDetailsInitResponse,
  QuoteDetailsSaveRequest,
  quoteDetailsSaveToDto,
  QuoteHousePlanTopList,
  QuotePrintRequest,
  QuotePrintResponse,
  QuoteVariationsIdsGetResponse,
} from './quote.model';
import { QuoteDialogEntryPoint } from '../../rfq-management/components/quote-details-dialog/quote-details-dialog.component';

export interface QuoteTopDialogConfig {
  housePlanId: ID;
  priceBookId: ID;
  formMode: number;
}

export interface QuoteDetailsDialogConfig {
  contactId: ID;
  customerName: string;
  salesFolderId: ID;
  targetQuoteId?: ID;
  entryPoint: QuoteDialogEntryPoint;
  targetQuoteVersion?: EntityVersion;
}

@Injectable()
export class QuoteDetailsBusinessService implements InitViewService {
  constructor(private quotationRest: QuotationRestService,
              private contactRest: ContactRestService,
              private siteRest: SiteRestService,
              private leadRest: LeadRestService,
              private salesFolderRest: SalesFolderRestService,
              private priceBookRest: PriceBookRestService,
              private topRest: TakeOffParamsRestService,
              private housePlanRest: HousePlanRestService,
              private letterRest: LetterRestService,
              private userContext: BuilderUserContext,
              private dictionaryService: DictionaryService,
              private gridLayoutService: GridLayoutService) {

  }

  get isCurrentUserSupportsProfit(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Franchisee);
  }

  initView(route: ActivatedRouteSnapshot): any {
    const sfId = getRouteParam(route, 'salesFolderId');
    const quoteId = getRouteParam(route, 'quoteId');

    const supportsProfit = this.isCurrentUserSupportsProfit;

    const quote$ = isRouteForCreationMode(route) ?
      this.quotationRest.getInit(sfId).pipe(map$(quoteDetailsInitFromDto))
      : this.get(sfId, quoteId);
    return {
      init: quote$,
      sites: this.siteRest.findAll(sfId).pipe(map$((list) => list.sites)),
      quotationBase: this.dictionaryService.getByEnum(QuotationBase),
      variationGridLayout: this.gridLayoutService.get(supportsProfit ? 'variationsWithProfit' : 'variations'),
    };
  }

  initQuoteTopDialog(snapshot: DialogRouteSnapshot<QuoteTopDialogConfig>): any {
    const {housePlanId} = snapshot.input;

    return {
      gtoUomDict: this.dictionaryService.getByEnum(GtoUom),
      tops: this.housePlanRest.findAllHousePlanTops(housePlanId).pipe(map$(housePlanTopListFromDto)),
    };
  }

  get(salesFolderId: ID, quoteId: ID): Observable<QuoteDetailsInitResponse> {
    return this.quotationRest.get(salesFolderId, quoteId).pipe(map$(quoteDetailsInitFromDto));
  }

  getLead(id: ID): Observable<LeadDetailsInit> {
    return this.leadRest.get(id).pipe(map$(leadDetailsInitFromDto));
  }

  findLeadsByName(query: FilteredDataQuery<string>, filters: any): Observable<LeadListItem[]> {
    return this.leadRest.findAllAsDataSource({
      autocomplete: true,
      searchString: query.filter,
      showOverdue: filters.showOverdue,
      showActive: filters.showActive,
      showCompleted: filters.showCompleted,
      showInactive: filters.showInactive,
    }).select(query).pipe(map$(getSlicedData));
  }

  findAllCloneSalesFoldersByCustomer(id: ID): Observable<CloneSalesFolder[]> {
    return this.salesFolderRest.findAllByLeadId(id);
  }

  findAllActiveSalesFoldersByCustomer(id: ID): Observable<CloneSalesFolder[]> {
    return this.salesFolderRest.findAllActiveByLeadId(id);
  }

  findAllCloneSitesBySalesFolder(id: ID): Observable<any> {
    return this.siteRest.findAll(id);
  }

  findAllCloneQuotesByCustomer(id: ID): Observable<CloneQuote[]> {
    return this.quotationRest.findAllBySalesFolderId(id).pipe(map$((response) => response.quotations));
  }

  save(quote: QuoteDetailsSaveRequest): Observable<QuoteDetailsInitResponse> {
    if (quote.quotationId) {
      return this.quotationRest.update(quoteDetailsSaveToDto(quote)).pipe(map$(quoteDetailsInitFromDto));
    } else {
      return this.quotationRest.create(quoteDetailsSaveToDto(quote)).pipe(map$(quoteDetailsInitFromDto));
    }
  }

  updateLead(leadDetails: any): Observable<any> {
    return  this.leadRest.update(leadDetails);
  }

  calculate(quote: QuoteDetailsSaveRequest): Observable<QuoteDetailsInitResponse> {
    return this.quotationRest.update(quoteDetailsSaveToDto(quote)).pipe(map$(quoteDetailsInitFromDto));
  }

  findPriceBooksByName(query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.priceBookRest.findAllAsAutocompleteDataSource({
                                                                searchString: query.filter,
                                                                usage: [PriceBookUsage.Normal],
                                                              }).select(query).pipe(
      map$(getSlicedData));
  }

  approve(sfId: ID, quotationId: ID, approve: QuoteApprove): Observable<QuoteDetailsInitResponse> {
    return this.quotationRest.approve(sfId, quotationId, approve).pipe(map$(quoteDetailsInitFromDto));
  }

  generateVariationIds(sfId: ID,
                       quotationId: ID,
                       componentsAmount: number,
                       itemsAmount: number): Observable<QuoteVariationsIdsGetResponse> {
    return this.quotationRest.generateVariationIds(sfId, quotationId, componentsAmount, itemsAmount);
  }

  generateVariationIdsHlp(hlpId: ID,
    componentsAmount: number,
    itemsAmount: number): Observable<QuoteVariationsIdsGetResponse> {
    return this.quotationRest.generateVariationIdsHlp(hlpId, componentsAmount, itemsAmount);
  }

  cloneQuote(sfId: ID, quotationId: ID, request: QuoteCloneRequest): Observable<QuoteDetailsInitResponse> {
    return this.quotationRest.clone(sfId, quotationId, request).pipe(map$(quoteDetailsInitFromDto));
  }

  cloneQuoteFromRfq(sfId: ID, quotationId: ID, siteId: ID, request: QuoteCloneRequest): Observable<any> {
    return this.quotationRest.cloneFromRfq(sfId, quotationId, siteId, request);
  }

  cloneVariations(sfId: ID,
                  quotationId: ID,
                  request: QuoteCloneVariationsRequest): Observable<QuoteDetailsInitResponse> {
    return this.quotationRest.cloneVariations(sfId, quotationId, request).pipe(map$(quoteDetailsInitFromDto));
  }

  updateTops(housePlanId: ID, request: QuoteHousePlanTopList): Observable<QuoteHousePlanTopList> {
    return this.housePlanRest.updateHousePlanTops(housePlanId,
                                                  {...housePlanTopListToDto(request), isPersist: true}).pipe(
      map$(housePlanTopListFromDto));
  }

  recalculateTops(housePlanId: ID, request: QuoteHousePlanTopList): Observable<QuoteHousePlanTopList> {
    return this.housePlanRest.updateHousePlanTops(housePlanId,
                                                  {...housePlanTopListToDto(request), isPersist: false}).pipe(
      map$(housePlanTopListFromDto));
  }

  print(request: QuotePrintRequest): Observable<QuotePrintResponse> {
    return this.letterRest.printQuote(request);
  }
}
